import type { NewsItem } from '../types/finance'
import { apiGet } from './api-client'

const USE_MOCK = !import.meta.env.VITE_FINANCE_API_URL

let _usingMockData = USE_MOCK
export function isNewsMock(): boolean { return _usingMockData }

// ─── Mock Data ───────────────────────────────────────────────

function mockNews(symbol: string): NewsItem[] {
  const now = new Date()
  const hours = (h: number) => new Date(now.getTime() - h * 3600000).toISOString()

  const newsPool: Record<string, NewsItem[]> = {
    AAPL: [
      { title: 'Apple Vision Pro sales exceed expectations in Q1, analysts upgrade price targets', url: '#', source: 'Bloomberg', publishedAt: hours(1), type: 'article', summary: 'Multiple Wall Street analysts raised their price targets after Apple reported stronger-than-expected Vision Pro sales figures.' },
      { title: 'Apple announces expanded AI features coming to iPhone 17 lineup', url: '#', source: 'Reuters', publishedAt: hours(3), type: 'article', summary: 'Apple previewed new on-device AI capabilities that will ship with the upcoming iPhone 17 series later this year.' },
      { title: '$AAPL breaking above 200 DMA with volume, institutional buyers stepping in. This is the setup we\'ve been waiting for.', url: '#', source: 'X', publishedAt: hours(2), type: 'tweet', author: '@MarketWatch', retweets: 128, likes: 890, isHighEngagement: true },
      { title: 'Apple supply chain checks suggest strong demand for MacBook Pro M5', url: '#', source: 'CNBC', publishedAt: hours(5), type: 'article', summary: 'Supply chain sources in Asia report increased component orders for the next MacBook Pro refresh.' },
      { title: 'Interesting flow in $AAPL April calls, 180 strike seeing massive volume today', url: '#', source: 'X', publishedAt: hours(4), type: 'tweet', author: '@unusual_whales', retweets: 67, likes: 340, isHighEngagement: true },
      { title: 'Apple Car project reportedly revived with new partnership', url: '#', source: 'Wall Street Journal', publishedAt: hours(8), type: 'article', summary: 'Sources familiar with the matter say Apple has entered discussions with a major automaker.' },
    ],
    MSFT: [
      { title: 'Microsoft Azure revenue grows 38% YoY driven by AI workloads', url: '#', source: 'Bloomberg', publishedAt: hours(2), type: 'article', summary: 'Azure cloud revenue surged as enterprise customers deployed more AI infrastructure on the platform.' },
      { title: '$MSFT Copilot adoption rate is insane. Every enterprise I talk to is rolling it out. This is a multi-year revenue driver.', url: '#', source: 'X', publishedAt: hours(1), type: 'tweet', author: '@TechFundManager', retweets: 89, likes: 520, isHighEngagement: true },
      { title: 'Microsoft expands Copilot to all Office 365 tiers', url: '#', source: 'Reuters', publishedAt: hours(6), type: 'article', summary: 'AI assistant Copilot will now be available to all commercial Office 365 subscribers at no extra cost.' },
      { title: 'Pentagon awards Microsoft $12B cloud contract expansion', url: '#', source: 'CNBC', publishedAt: hours(10), type: 'article', summary: 'The Department of Defense extended its JEDI successor contract with significant scope increases.' },
    ],
    GOOGL: [
      { title: 'Alphabet reports record Search revenue as AI Overviews drive engagement', url: '#', source: 'Bloomberg', publishedAt: hours(3), type: 'article', summary: 'Google Search revenue hit new highs as AI-powered features increased user time on platform.' },
      { title: '$GOOGL Waymo expansion to 10 new cities this year is massively undervalued. Robotaxi TAM alone justifies current price.', url: '#', source: 'X', publishedAt: hours(2), type: 'tweet', author: '@ARKInvest', retweets: 156, likes: 1200, isHighEngagement: true },
      { title: 'Google DeepMind achieves breakthrough in protein design', url: '#', source: 'Nature', publishedAt: hours(7), type: 'article', summary: 'New AlphaFold model can design novel proteins with therapeutic applications.' },
    ],
    TSLA: [
      { title: 'Tesla deliveries beat estimates with 500K vehicles in Q1', url: '#', source: 'Reuters', publishedAt: hours(1), type: 'article', summary: 'Tesla exceeded delivery expectations driven by strong Model Y and Cybertruck demand.' },
      { title: '$TSLA robotaxi event confirmed for June. If FSD v13 performs as leaked videos suggest, this stock is going to $400+', url: '#', source: 'X', publishedAt: hours(3), type: 'tweet', author: '@ElectrekCo', retweets: 234, likes: 1800, isHighEngagement: true },
      { title: 'Tesla Energy division revenue doubles, margins expand to 25%', url: '#', source: 'CNBC', publishedAt: hours(6), type: 'article', summary: 'Megapack deployments and Powerwall demand drove Tesla Energy to record profitability.' },
      { title: 'Short interest in $TSLA dropping fast. Bears are capitulating. Volume confirms the move.', url: '#', source: 'X', publishedAt: hours(5), type: 'tweet', author: '@SqueezeMetrics', retweets: 45, likes: 280, isHighEngagement: false },
    ],
  }

  // Return specific news if available, otherwise generate generic
  if (newsPool[symbol.toUpperCase()]) {
    return newsPool[symbol.toUpperCase()]
  }

  return [
    { title: `${symbol.toUpperCase()} reports earnings ahead of analyst estimates`, url: '#', source: 'Bloomberg', publishedAt: hours(2), type: 'article', summary: `${symbol.toUpperCase()} beat consensus EPS estimates by 8% in the latest quarterly report.` },
    { title: `Analysts upgrade ${symbol.toUpperCase()} citing strong forward guidance`, url: '#', source: 'Reuters', publishedAt: hours(5), type: 'article', summary: 'Multiple analysts raised their outlook following management commentary on growth prospects.' },
    { title: `$${symbol.toUpperCase()} looking strong technically. Breaking out of consolidation with increasing volume.`, url: '#', source: 'X', publishedAt: hours(3), type: 'tweet', author: '@ChartAnalyst', retweets: 32, likes: 180, isHighEngagement: false },
  ]
}

function mockTrendingNews(): NewsItem[] {
  const now = new Date()
  const hours = (h: number) => new Date(now.getTime() - h * 3600000).toISOString()
  return [
    { title: 'Fed officials signal patience on further cuts as inflation data firms', url: '#', source: 'Bloomberg', publishedAt: hours(1), type: 'article', summary: 'Several FOMC members pushed back on market pricing for near-term easing, citing sticky services inflation.' },
    { title: 'Chip stocks extend rally as hyperscalers raise AI capex guidance again', url: '#', source: 'Reuters', publishedAt: hours(2), type: 'article', summary: 'Semiconductor names led the market after two major cloud providers lifted infrastructure spending plans.' },
    { title: 'Breadth is the story today — equal-weight S&P outperforming cap-weight by the most since March. Rotation, not risk-off.', url: '#', source: 'X', publishedAt: hours(2), type: 'tweet', author: '@MacroCharts', retweets: 210, likes: 1400, isHighEngagement: true },
    { title: 'Treasury yields climb after strong durable-goods print', url: '#', source: 'CNBC', publishedAt: hours(4), type: 'article', summary: 'The 10-year yield rose as the data pointed to resilient business investment.' },
    { title: 'Dollar softens against majors ahead of Friday jobs report', url: '#', source: 'Wall Street Journal', publishedAt: hours(5), type: 'article', summary: 'FX desks positioned for a softer payrolls number after weak ADP data.' },
    { title: 'Unusual options flow: massive call sweeps hitting the tape in energy names. Someone is positioning ahead of OPEC.', url: '#', source: 'X', publishedAt: hours(6), type: 'tweet', author: '@unusual_whales', retweets: 96, likes: 610, isHighEngagement: true },
  ]
}

/**
 * The deployed news route currently returns rows with every field blank (an
 * upstream parsing bug), so strip anything without a title before rendering —
 * an all-blank live feed should fall through to mock data, not empty cards.
 */
function validNews(items: NewsItem[]): NewsItem[] {
  if (!Array.isArray(items)) return []
  return items.filter((n) => n && typeof n.title === 'string' && n.title.trim().length > 0)
}

/** Merge per-symbol feeds into one trending list: high-engagement first, then recency. */
function rankTrending(items: NewsItem[], cap: number): NewsItem[] {
  const seen = new Set<string>()
  const deduped = items.filter((n) => {
    const key = n.title.toLowerCase()
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
  return deduped
    .sort((a, b) => {
      const eng = Number(b.isHighEngagement ?? false) - Number(a.isHighEngagement ?? false)
      if (eng !== 0) return eng
      return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
    })
    .slice(0, cap)
}

// ─── API Functions ───────────────────────────────────────────

export async function getNews(symbol: string): Promise<NewsItem[]> {
  if (USE_MOCK) {
    _usingMockData = true
    await new Promise((r) => setTimeout(r, 600))
    return mockNews(symbol)
  }
  try {
    const result = validNews(await apiGet<NewsItem[]>(`/api/finance/news?symbol=${encodeURIComponent(symbol)}`))
    if (result.length === 0) throw new Error('live news feed empty')
    _usingMockData = false
    return result
  } catch {
    console.warn('News API failed, falling back to mock data')
    _usingMockData = true
    return mockNews(symbol)
  }
}

/**
 * Market-wide trending feed for the front dashboard. Tries the (future)
 * trending route, then aggregates the per-symbol news feeds for the supplied
 * symbols — typically the day's biggest movers — before falling back to mock.
 */
export async function getTrendingNews(symbols: string[] = []): Promise<NewsItem[]> {
  if (USE_MOCK) {
    _usingMockData = true
    await new Promise((r) => setTimeout(r, 500))
    return mockTrendingNews()
  }
  try {
    const result = validNews(await apiGet<NewsItem[]>('/api/finance/news/trending'))
    if (result.length === 0) throw new Error('live trending feed empty')
    _usingMockData = false
    return rankTrending(result, 8)
  } catch {
    // Trending route not deployed yet — aggregate per-symbol feeds instead.
  }
  const pool = symbols.length > 0 ? symbols.slice(0, 5) : ['AAPL', 'NVDA', 'TSLA', 'MSFT']
  try {
    const settled = await Promise.allSettled(
      pool.map((s) => apiGet<NewsItem[]>(`/api/finance/news?symbol=${encodeURIComponent(s)}`))
    )
    const merged = validNews(settled.flatMap((s) => (s.status === 'fulfilled' ? s.value : [])))
    if (merged.length === 0) throw new Error('no live news')
    _usingMockData = false
    return rankTrending(merged, 8)
  } catch {
    console.warn('Trending news aggregation failed, falling back to mock data')
    _usingMockData = true
    return mockTrendingNews()
  }
}
